'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import type {
  GlycerinCheckboxClickPayload,
  GTMEventGlycerinCheckboxClick,
} from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { cn } from '../../utils/cn';

interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  error?: boolean;
  errorMessage?: string;
  clickTrackingProps?: Omit<GlycerinCheckboxClickPayload, 'isChecked'> | null;
}
const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  (
    { error, errorMessage, checked, disabled, className, onClick, clickTrackingProps, ...props },
    ref,
  ) => {
    const { category, label, detail, placement, variantIds, custom } = clickTrackingProps ?? {};
    const dispatchGTMEvent = useTracking();
    return (
      <>
        <CheckboxPrimitive.Root
          ref={ref}
          className={cn(
            'border-color-primary h-2.5 w-2.5 shrink-0 rounded-md border focus-visible:outline-none' +
              ' disabled:bg-color-grey-light disabled:border-color-grey',
            className,
            error && 'border-color-error',
          )}
          disabled={disabled}
          checked={checked}
          onClick={(event) => {
            if (clickTrackingProps !== null) {
              dispatchGTMEvent<GTMEventGlycerinCheckboxClick>({
                event: 'CheckboxClick',
                CheckboxClickData: {
                  category: category ?? 'checkbox-click',
                  label,
                  detail,
                  placement,
                  isChecked: !checked,
                  variantIds,
                  custom,
                },
              });
            }
            if (onClick) {
              onClick(event);
            }
          }}
          {...props}
        >
          <CheckboxPrimitive.Indicator
            className={cn('data-[disabled]:text-color-grey flex items-center justify-center')}
          >
            <span className="icon-emp-checkmark h-2.5 w-2.5" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {error && errorMessage && (
          <div
            className={cn(
              'text-size-body2 flex items-center gap-[0.1] pl-[2px]',
              error && 'text-color-error',
            )}
          >
            <span className="icon-emp-disadvantage shrink-0" />
            {errorMessage}
          </div>
        )}
      </>
    );
  },
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
